<template>
  <select class="dropdown" :class="classes" :value="displayValue" :id="inputId" @change="updateModel" :disabled="isDisabled"
    :style="styleObject">
    <option v-for="item, i in items" :key="i" :value="item">
      {{ item }}
    </option>
  </select>
  <validation-label ref="label" :elementId="inputId" :text="description" :abbreviatedText="abbreviatedDescription" />
</template>

<script setup lang="ts">
import { ref, PropType, watchEffect } from 'vue';
import ValidationLabel from '@/shared/components/ValidationLabel.vue';
import { generateUUID, isNullOrWhitespace } from '@/guiide/ts/utilities';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: [String, null] as PropType<string | null>,
    default: null
  },
  items: { type: Array<string>, required: true },
  isDisabled: { type: Boolean, default: false },
  description: { type: String, default: "" },
  abbreviatedDescription: { type: String, default: "" },
  optional: { type: Boolean, default: false },
  isFormControl: { type: Boolean, default: false },
  classes: { type: String, default: "" },
  styleObject: Object as PropType<Record<string, string | number>>
});

defineExpose({ isValid });

const inputId = `input-${generateUUID()}`;
const displayValue = ref(props.modelValue?.toString() ?? '');
const label = ref<typeof ValidationLabel>(null);

const updateModel = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;

  if (value === "") {
    emit('update:modelValue', null);
    return;
  }

  emit('update:modelValue', value);
};

watchEffect(() => {
  if (props.modelValue === null) {
    displayValue.value = "";
    return;
  }

  displayValue.value = props.modelValue?.toString() ?? '';
});

function isValid(): boolean {
  label.value.hideAnyWarnings();

  if (props.optional) {
    return true;
  }

  if (isNullOrWhitespace(displayValue.value)) {
    label.value.warn('*');
    return false;
  }

  return true;
}
</script>
